const TRANSFER_LOCK_NAME = 'bytebeam-firmware-transfer'

// How long to wait before assuming the lock is held elsewhere and firing onWaiting.
// Low enough that a genuinely queued transfer is reported promptly, high enough that a
// same-tick grant (the common case) never triggers a spurious "waiting" notice.
const WAITING_NOTICE_DELAY_MS = 200

/**
 * Serializes firmware/file uploads and downloads across every tab of this origin.
 *
 * A large upload and a large download running at the same time compete for the same
 * physical connection — the upload can saturate the link (and, on asymmetric connections,
 * starve the ACKs a concurrent download needs), causing the download to stall indefinitely
 * with nothing to time out against. Running transfers one at a time removes that contention
 * instead of just detecting or timing out on it.
 */
export const withTransferLock = async <T>(
	task: () => Promise<T>,
	options?: { signal?: AbortSignal; onWaiting?: () => void },
): Promise<T> => {
	if (typeof navigator === 'undefined' || !navigator.locks) {
		return task()
	}

	let acquired = false
	const waitTimer = setTimeout(() => {
		if (!acquired) {
			options?.onWaiting?.()
		}
	}, WAITING_NOTICE_DELAY_MS)

	const lockOptions: LockOptions = options?.signal ? { signal: options.signal } : {}

	try {
		return await navigator.locks.request(TRANSFER_LOCK_NAME, lockOptions, () => {
			acquired = true
			clearTimeout(waitTimer)
			return task()
		})
	} finally {
		clearTimeout(waitTimer)
	}
}

/** True for an AbortError/ERR_CANCELED raised by a user-initiated cancel (including while queued on the transfer lock). */
export const isUserCanceledError = (error: unknown): boolean => {
	if (error instanceof DOMException && error.name === 'AbortError') {
		return true
	}
	const code = (error as { code?: string } | null)?.code
	return code === 'ERR_CANCELED'
}

/**
 * True while a firmware/file upload or download (see withTransferLock) is in flight, in this tab or
 * another. Callers use this to relax a request's timeout only when contention is actually plausible,
 * instead of blanket-relaxing it for every call.
 */
export const isHeavyTransferInProgress = async (): Promise<boolean> => {
	if (typeof navigator === 'undefined' || !navigator.locks?.query) {
		return false
	}
	try {
		const { held } = await navigator.locks.query()
		return (held ?? []).some(lock => lock.name === TRANSFER_LOCK_NAME)
	} catch {
		// query() isn't supported everywhere request() is (e.g. some Safari versions) — fail open
		// to the caller's default (fast) timeout rather than guessing.
		return false
	}
}
